import React, { useState, useCallback } from 'react';
import { Box, Tooltip, SxProps, Theme } from '@mui/material';
import { copyToClipboard } from '../../utils/copyToClipboard';

export interface CopyableTextProps {
  /** הערך שיועתק ללוח */
  text: string;
  children?: React.ReactNode;
  tooltipCopy?: string;
  tooltipCopied?: string;
  sx?: SxProps<Theme>;
  onClick?: (e: React.MouseEvent) => void;
}

const CopyableText: React.FC<CopyableTextProps> = ({
  text,
  children,
  tooltipCopy = 'לחץ להעתקה',
  tooltipCopied = 'הועתק!',
  sx,
  onClick,
}) => {
  const [copied, setCopied] = useState(false);
  const canCopy = Boolean(String(text || '').trim());

  const handleClick = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      onClick?.(e);
      if (!canCopy) return;
      const ok = await copyToClipboard(text);
      if (ok) {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      }
    },
    [canCopy, onClick, text],
  );

  const content = (
    <Box
      component="span"
      role={canCopy ? 'button' : undefined}
      tabIndex={canCopy ? 0 : undefined}
      onKeyDown={
        canCopy
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                void handleClick(e as unknown as React.MouseEvent);
              }
            }
          : undefined
      }
      onClick={canCopy ? handleClick : undefined}
      sx={{
        ...(canCopy && {
          cursor: 'pointer',
          '&:hover': { textDecoration: 'underline', textUnderlineOffset: 2 },
        }),
        ...sx,
      }}
    >
      {children ?? text}
    </Box>
  );

  if (!canCopy) return content;

  return (
    <Tooltip title={copied ? tooltipCopied : tooltipCopy} arrow placement="top">
      {content}
    </Tooltip>
  );
};

export default CopyableText;
